import _ from 'lodash';

const HISTORY_KEY = 'lookup-history';
const MAX_ENTRIES = 500;

interface LocalHistoryInput {
  id: string;
  name?: string;
  email?: string;
  type: string;
  query?: Record<string, string>;
  description?: string;
}

export interface HistoryEntry {
  count: number;
  id: string;
  type: string;
  value: string;
  query?: Record<string, string>;
  description?: string;
  lastSearched: number;
  action: undefined;
}

const hasLocalStorage = (function () {
  let checked = false;
  let detected = false;
  return function () {
    if (!process.browser) {
      return false;
    }
    if (!checked) {
      try {
        window.localStorage.setItem('__test', '1');
        window.localStorage.removeItem('__test');
        detected = true;
      } finally {
        checked = true;
      }
    }
    return detected;
  };
})();

export const getHistory = (): HistoryEntry[] => {
  if (!hasLocalStorage || typeof window === 'undefined') {
    return [];
  }

  const itemsStr = window.localStorage.getItem(HISTORY_KEY);
  const items = itemsStr ? JSON.parse(itemsStr) : [];
  return _.filter(items, item => item.id && item.type && item.value && item.count);
};

// there may be concurrency issues (with other tabs), but whatever
export const updateHistory = (obj: LocalHistoryInput) => {
  if (!hasLocalStorage || typeof window === 'undefined') {
    return;
  }

  const { id, name, email, type, query, description } = obj;
  let items = getHistory();
  const idx = _.findIndex(items, item => item.id === id && _.isEqual(item.query, query));
  const now = new Date().getTime();
  if (idx === -1) {
    items.push({
      id,
      query,
      type,
      value: name || email || '',
      count: 1,
      description,
      lastSearched: now,
      action: undefined,
    });
  } else {
    const [oldItem] = items.splice(idx, 1);
    oldItem.count += 1;
    oldItem.query = query;
    oldItem.description = description;
    oldItem.lastSearched = now;
    items.push(oldItem);
  }

  // keep most recently used entries under max
  if (items.length > MAX_ENTRIES) {
    items = _.sortBy(items, item => -item.lastSearched).slice(0, MAX_ENTRIES);
  }

  try {
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify(items));
  } catch (e) {
    window.localStorage.clear();
  }
};
